import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Loader2, Clock, CalendarIcon, User, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { format, addDays, isPast, startOfDay, isToday } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { buildBookingAvailabilityViewModel, formatUKTime } from './bookingAvailabilityViewModel';
import { decideBookingPhone } from './bookingPhone';
import { isPhoneMissing } from '@shared/phone-quality';
import type {
  AvailabilityResponse,
  CalendarConflict,
  TimeSlot,
} from './bookingAvailabilityViewModel';

interface Staff {
  name: string;
  email: string;
}

type BookingType = 'design_call' | 'site_visit';

interface Customer {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  postcode?: string;
  opensolarProjectId?: string;
  dealValue?: number;
}

interface PipelineBookingPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: Customer;
  onBookingComplete?: (date: Date, staffName: string, bookingType: BookingType) => void;
}

const BOOKING_TYPE_LABELS: Record<BookingType, string> = {
  design_call: 'Design Call',
  site_visit: 'Site Visit',
};

const BOOKING_DURATIONS: Record<BookingType, string> = {
  design_call: '30 min',
  site_visit: '1h 30m',
};

export function PipelineBookingPopup({
  open,
  onOpenChange,
  customer,
  onBookingComplete,
}: PipelineBookingPopupProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedStaff, setSelectedStaff] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [bookingType, setBookingType] = useState<BookingType>('design_call');
  const [completionChannel, setCompletionChannel] = useState<'phone' | 'email' | 'in_person' | 'unknown'>('unknown');
  const bookingRequests = useRef(new Map<string, string>());
  // A missing phone is asked for at booking, when staff have spoken to the customer.
  const needsPhone = isPhoneMissing(customer.phone);
  const [bookingPhone, setBookingPhone] = useState('');
  const [phoneDeclined, setPhoneDeclined] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState<string | null>(null);
  const [savingPhone, setSavingPhone] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch available staff
  const { data: staffData } = useQuery<{ success: boolean; staff: Staff[] }>({
    queryKey: ['/api/pipeline/booking/staff'],
    enabled: open,
  });

  // Fetch availability for selected staff and date
  const {
    data: availabilityData,
    isLoading: loadingAvailability,
    error: availabilityError,
  } = useQuery<AvailabilityResponse>({
    queryKey: [
      '/api/pipeline/booking/availability',
      selectedStaff,
      format(selectedDate, 'yyyy-MM-dd'),
      bookingType,
    ],
    queryFn: async () => {
      const response = await fetch(
        `/api/pipeline/booking/availability/${encodeURIComponent(selectedStaff)}?date=${format(selectedDate, 'yyyy-MM-dd')}&type=${bookingType}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }
      return response.json();
    },
    enabled: open && !!selectedStaff,
    // Calendars change between bookings; never offer slots from a cached list.
    staleTime: 0,
  });
  const refreshAvailability = () =>
    queryClient.invalidateQueries({ queryKey: ['/api/pipeline/booking/availability'] });

  // Create booking mutation
  const createBookingMutation = useMutation({
    mutationFn: async (data: {
      customerId: number;
      staffName: string;
      bookingType: BookingType;
      slotStart: string;
      updateNextContact: boolean;
      requestId: string;
      completionChannel: 'phone' | 'email' | 'in_person' | 'unknown';
    }) => {
      const response = await fetch('/api/pipeline/booking/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to create booking');
      return result;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: 'Booking Created',
          description: `${BOOKING_TYPE_LABELS[bookingType]} booked with ${selectedStaff}`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
        queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
        refreshAvailability();
        if (onBookingComplete && selectedSlot) {
          onBookingComplete(new Date(selectedSlot.start), selectedStaff, bookingType);
        }
        onOpenChange(false);
      } else if (data.skipped) {
        toast({
          title: 'Booking Skipped',
          description: data.skipReason || 'Calendar booking was skipped',
          variant: 'destructive',
        });
      }
    },
    onError: (error) => {
      toast({
        title: 'Booking Failed',
        description: error instanceof Error ? error.message : 'Failed to create booking',
        variant: 'destructive',
      });
      // The slot may have been taken since the list loaded: reload and re-pick.
      setSelectedSlot(null);
      refreshAvailability();
    },
  });

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedSlot(null);
      setSelectedDate(new Date());
      setBookingType('design_call');
      setCompletionChannel('unknown');
      setBookingPhone('');
      setPhoneDeclined(false);
      setPhoneMessage(null);
    }
  }, [open]);

  // Set initial staff when staff load
  useEffect(() => {
    if (staffData?.staff?.length && !selectedStaff) {
      setSelectedStaff(staffData.staff[0].name);
    }
  }, [staffData, selectedStaff]);

  // Reset slot when booking type changes
  useEffect(() => {
    setSelectedSlot(null);
  }, [bookingType, selectedDate, selectedStaff]);

  const handleConfirm = async () => {
    if (selectedSlot && selectedStaff) {
      const phoneDecision = decideBookingPhone({
        currentPhone: customer.phone,
        enteredPhone: bookingPhone,
        declined: phoneDeclined,
      });
      if (!phoneDecision.ok) {
        setPhoneMessage(phoneDecision.message);
        return;
      }
      if (phoneDecision.phoneToSave) {
        setSavingPhone(true);
        try {
          const response = await fetch(`/api/customers/${customer.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ phone: phoneDecision.phoneToSave }),
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            setPhoneMessage(body.message || 'Could not save the phone number. Please try again.');
            return;
          }
          queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
          queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
        } finally {
          setSavingPhone(false);
        }
      }
      const key = JSON.stringify([customer.id, selectedStaff, bookingType, selectedSlot.start, completionChannel]);
      let requestId = bookingRequests.current.get(key);
      if (!requestId) {
        requestId = crypto.randomUUID();
        bookingRequests.current.set(key, requestId);
      }
      createBookingMutation.mutate({
        customerId: customer.id,
        staffName: selectedStaff,
        bookingType,
        slotStart: selectedSlot.start,
        updateNextContact: true,
        requestId,
        completionChannel,
      });
    }
  };

  // Calculate the date range for the calendar (30 days ahead)
  const minDate = new Date();
  const maxDate = addDays(new Date(), 30);

  // Disable past dates
  const disabledDays = (date: Date) => {
    return isPast(startOfDay(date)) && !isToday(date);
  };

  const availabilityView = buildBookingAvailabilityViewModel(availabilityData);

  const formatTimeRange = (start: string, end: string) => {
    return `${formatUKTime(start)} - ${formatUKTime(end)}`;
  };

  const renderConflictRow = (conflict: CalendarConflict) => (
    <div
      key={conflict.id}
      className="rounded-md border bg-muted/30 p-3 text-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium leading-snug break-words">{conflict.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatTimeRange(conflict.start, conflict.end)}
          </p>
          {conflict.location && (
            <p className="mt-1 text-xs text-muted-foreground break-words">
              {conflict.location}
            </p>
          )}
        </div>
        <Badge variant="outline" className="shrink-0 text-xs">
          Busy
        </Badge>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Book Appointment
          </DialogTitle>
          <DialogDescription>
            Schedule a design call or site visit with {customer.name}
          </DialogDescription>
        </DialogHeader>

        {/* Customer Info Banner */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="truncate">{customer.name}</span>
            </div>
            {customer.phone && (
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3 text-muted-foreground" />
                <span className="truncate">{customer.phone}</span>
              </div>
            )}
            {customer.email && (
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3 text-muted-foreground" />
                <span className="truncate">{customer.email}</span>
              </div>
            )}
            {customer.address && (
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3 text-muted-foreground" />
                <span className="truncate">{customer.address}</span>
              </div>
            )}
          </div>
          {customer.opensolarProjectId && (
            <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
              <ExternalLink className="h-3 w-3" />
              <span>OpenSolar project linked</span>
            </div>
          )}
        </div>

        {/* Booking Type Selection */}
        <Tabs
          value={bookingType}
          onValueChange={(v) => setBookingType(v as BookingType)}
          className="mb-4"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="design_call" className="flex items-center gap-2">
              Design Call
              <Badge variant="secondary" className="text-xs">
                {BOOKING_DURATIONS.design_call}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="site_visit" className="flex items-center gap-2">
              Site Visit
              <Badge variant="secondary" className="text-xs">
                {BOOKING_DURATIONS.site_visit}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left side: Staff selection and Calendar */}
          <div className="space-y-4">
            {/* Staff Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Staff</label>
              <div className="flex gap-2">
                {staffData?.staff?.map((staff) => (
                  <Button
                    key={staff.email}
                    variant={selectedStaff === staff.name ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => {
                      setSelectedStaff(staff.name);
                      setSelectedSlot(null);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {staff.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="border rounded-lg p-3">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    setSelectedDate(date);
                    setSelectedSlot(null);
                  }
                }}
                disabled={disabledDays}
                fromDate={minDate}
                toDate={maxDate}
                className="rounded-md"
              />
            </div>

            {/* Selected date display */}
            <div className="text-center text-sm text-muted-foreground">
              {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </div>
          </div>

          {/* Right side: Time slots */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Available Times</label>
              {!availabilityData?.msGraphAvailable && availabilityData?.success && (
                <Badge variant="outline" className="text-xs">
                  Calendar offline - availability unchecked
                </Badge>
              )}
            </div>

            <ScrollArea className="h-[360px] md:h-[430px] rounded-lg border">
              <div className="flex flex-col gap-4 p-4">
                {!selectedStaff ? (
                  <div className="flex min-h-[280px] items-center justify-center text-muted-foreground">
                    Please select a staff member
                  </div>
                ) : loadingAvailability ? (
                  <div className="flex min-h-[280px] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : availabilityError ? (
                  <div className="flex min-h-[280px] items-center justify-center text-red-500">
                    Failed to load availability
                  </div>
                ) : availabilityData?.slots ? (
                  <>
                    <section className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">Booked in calendar</p>
                          <p className="text-xs text-muted-foreground">
                            {selectedStaff} on {format(selectedDate, 'EEE, MMM d')}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {availabilityView.conflictRows.length}
                        </Badge>
                      </div>

                      {availabilityView.conflictRows.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {availabilityView.conflictRows.map(renderConflictRow)}
                        </div>
                      ) : (
                        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                          No calendar bookings returned for this date.
                        </div>
                      )}
                    </section>

                    <Separator />

                    <section className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">Available slots</p>
                          <p className="text-xs text-muted-foreground">
                            {availabilityView.unavailableSlotCount > 0
                              ? `${availabilityView.unavailableSlotCount} unavailable or past slots hidden`
                              : 'All returned slots are available'}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {availabilityView.availableSlots.length}
                        </Badge>
                      </div>

                      {availabilityView.availableSlots.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          {availabilityView.availableSlots.map((slot, index) => {
                            const isSelected = selectedSlot?.start === slot.start;

                            return (
                              <Button
                                key={`${slot.start}-${index}`}
                                variant={isSelected ? 'default' : 'outline'}
                                className="h-auto py-3 flex flex-col items-center justify-center gap-1"
                                onClick={() => setSelectedSlot(slot)}
                              >
                                <Clock className="h-4 w-4" />
                                <span className="text-sm font-medium">
                                  {formatUKTime(slot.start)}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                  {BOOKING_DURATIONS[bookingType]}
                                </span>
                              </Button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                          No available slots for this staff member and booking type.
                        </div>
                      )}
                    </section>
                  </>
                ) : (
                  <div className="flex min-h-[280px] items-center justify-center text-muted-foreground">
                    No slots available
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Selection summary and confirm button */}
            {selectedSlot && (
              <div className="space-y-3 pt-2 border-t">
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <p className="text-sm font-medium">Selected Appointment</p>
                  <p className="text-sm text-muted-foreground">
                    {BOOKING_TYPE_LABELS[bookingType]} with {selectedStaff}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {format(selectedDate, 'EEE, MMM d')} at{' '}
                    {formatUKTime(selectedSlot.start)} ({BOOKING_DURATIONS[bookingType]})
                  </p>
                </div>
                {needsPhone && (
                  <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
                    <label className="block space-y-1">
                      <span className="font-medium">Customer's phone number</span>
                      <input
                        type="tel"
                        className="w-full rounded-md border bg-background p-2"
                        placeholder="e.g., 07911 123456"
                        value={bookingPhone}
                        disabled={phoneDeclined || savingPhone || createBookingMutation.isPending}
                        onChange={(event) => {
                          setBookingPhone(event.target.value);
                          setPhoneMessage(null);
                        }}
                      />
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Saved to the customer so the whole team can reach them.
                    </p>
                    <label className="flex w-fit cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={phoneDeclined}
                        onChange={(event) => {
                          setPhoneDeclined(event.target.checked);
                          setPhoneMessage(null);
                        }}
                      />
                      Customer won't share a number
                    </label>
                    {phoneMessage && <p className="text-xs text-red-600">{phoneMessage}</p>}
                  </div>
                )}
                <label className="block space-y-1 text-sm">
                  <span>How did the customer confirm this appointment?</span>
                  <select
                    className="w-full rounded-md border bg-background p-2"
                    value={completionChannel}
                    onChange={event => setCompletionChannel(event.target.value as typeof completionChannel)}
                    disabled={createBookingMutation.isPending}
                  >
                    <option value="unknown">Not recorded</option>
                    <option value="phone">Phone call</option>
                    <option value="email">Email</option>
                    <option value="in_person">In person</option>
                  </select>
                </label>
                <Button
                  className="w-full"
                  onClick={handleConfirm}
                  disabled={!selectedSlot || savingPhone || createBookingMutation.isPending}
                >
                  {savingPhone || createBookingMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Booking...
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
